import type { BeneficiaryContactCreationFailedAction } from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Actions/beneficiaryContactCreationFailed';
import type { BeneficiaryContactUpdateFailedAction } from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Actions/beneficiaryContactUpdateFailed';
import CreateBeneficiaryContact from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Commands/CreateBeneficiaryContact';
import UpdateBeneficiaryContact from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Commands/UpdateBeneficiaryContact';
import createBeneficiaryContact from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/createBeneficiaryContact';
import updateBeneficiaryContact from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/updateBeneficiaryContact';
import FetchContactRelationships from '@Application/Beneficiaries/BeneficiaryContacts/ContactRelationships/Queries/FetchContactRelationships';
import fetchContactRelationships from '@Application/Beneficiaries/BeneficiaryContacts/ContactRelationships/fetchContactRelationships';
import FetchContactRoles from '@Application/Beneficiaries/BeneficiaryContacts/ContactRoles/Queries/FetchContactRoles';
import fetchContactRoles from '@Application/Beneficiaries/BeneficiaryContacts/ContactRoles/fetchContactRoles';
import FormSection from '@Guidelines/Component/FormSection';
import BeneficiaryContactFormData from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/Sidebars/UpsertContactSidebar/FormData/BeneficiaryContactFormData';
import contactSidebarSignal, {
	closeContactSidebar,
} from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/BeneficiaryContacts/Signals/contactSidebarSignal';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import {
	Button,
	Checkbox,
	ConfirmDialog,
	Dropdown,
	Form,
	InputText,
	Message,
	Sidebar,
	TextArea,
} from '@verclam/ui-kit';

const OTHER_ROLE_VALUE = 'other';

/**
 * One panel for both creation and edition of a dossier's contact, told apart by `contactId` on the
 * signal's context — same shape as `UpsertBeneficiarySidebar`. Unlike that sidebar, the edited contact
 * is read straight from `beneficiaryContactsStore.contacts` (already fetched in full by the owning
 * screen) rather than refetched individually: there is no `GET .../contacts/:contact` route.
 */
const UpsertContactSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const params = useParams<{ beneficiary: string }>();
	const beneficiaryId = params.beneficiary !== undefined ? Number(params.beneficiary) : Number.NaN;
	const context = useSignalValue(contactSidebarSignal);
	const { currentFacility } = useStrictCurrentFacility();

	const contacts = useSelector((state) => state.beneficiaryContactsStore.contacts);

	const [formData, setFormData] = React.useState(() => BeneficiaryContactFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [isRevocationConfirmVisible, setIsRevocationConfirmVisible] = React.useState(false);

	const formId = React.useId();
	const contactId = context?.contactId ?? null;
	const isEditMode = contactId !== null;

	const { data: contactRoles } = useStoreQuery({
		selector: (state) => state.contactRolesStore.contactRoles,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchContactRoles(new FetchContactRoles())),
	});

	const { data: contactRelationships } = useStoreQuery({
		selector: (state) => state.contactRelationshipsStore.contactRelationships,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchContactRelationships(new FetchContactRelationships())),
	});

	const editedContact = contacts?.findById(contactId) ?? null;

	// Another contact already carrying the designation: a confort preview of the `422` the server would
	// otherwise refuse with — it reduces the occurrence of that rejection, it never replaces it.
	const existingTrustHolder = React.useMemo(() => {
		if (contacts === null) {
			return null;
		}

		return contacts.list.find((contact) => contact.isPersonOfTrust && contact.id !== contactId) ?? null;
	}, [contacts, contactId]);

	React.useEffect(() => {
		if (context === null) {
			return;
		}

		if (editedContact !== null) {
			setFormData(BeneficiaryContactFormData.fromView(editedContact));
			return;
		}

		if (!isEditMode) {
			setFormData(BeneficiaryContactFormData.createEmpty());
		}
	}, [context, editedContact, isEditMode]);

	const handleFirstNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setFirstName(value));
	}, []);

	const handleLastNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setLastName(value));
	}, []);

	const handleRoleChange = React.useCallback((value: string | null) => {
		setFormData((current) => current.setRole(value));
	}, []);

	const handleIsPersonOfTrustChange = React.useCallback((value: boolean) => {
		setFormData((current) => current.setIsPersonOfTrust(value));
	}, []);

	const handleRoleDetailsChange = React.useCallback((value: string) => {
		setFormData((current) => current.setRoleDetails(value));
	}, []);

	const handleRelationshipChange = React.useCallback((value: string | null) => {
		setFormData((current) => current.setRelationship(value));
	}, []);

	const handlePhoneNumberChange = React.useCallback((value: string) => {
		setFormData((current) => current.setPhoneNumber(value));
	}, []);

	const handleEmailChange = React.useCallback((value: string) => {
		setFormData((current) => current.setEmail(value));
	}, []);

	const handleNotesChange = React.useCallback((value: string) => {
		setFormData((current) => current.setNotes(value));
	}, []);

	const performSubmit = React.useCallback(() => {
		if (Number.isNaN(beneficiaryId) || formData.relationship === null) {
			return;
		}

		const facilityId = currentFacility.id;
		const roleDetails = formData.role === OTHER_ROLE_VALUE ? formData.roleDetails : null;
		const phoneNumber = formData.phoneNumber === '' ? null : formData.phoneNumber;
		const email = formData.email === '' ? null : formData.email;
		const notes = formData.notes === '' ? null : formData.notes;

		setIsSubmitting(true);

		if (contactId === null) {
			dispatch(
				createBeneficiaryContact(
					new CreateBeneficiaryContact(
						facilityId,
						beneficiaryId,
						formData.firstName,
						formData.lastName,
						formData.role,
						formData.isPersonOfTrust,
						roleDetails,
						formData.relationship,
						phoneNumber,
						email,
						notes,
					),
				),
			)
				.then(() => closeContactSidebar())
				.catch((action: BeneficiaryContactCreationFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'BENEFICIARY_CONTACT_CREATION_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(
			updateBeneficiaryContact(
				new UpdateBeneficiaryContact(
					facilityId,
					beneficiaryId,
					contactId,
					formData.firstName,
					formData.lastName,
					formData.role,
					formData.isPersonOfTrust,
					roleDetails,
					formData.relationship,
					phoneNumber,
					email,
					notes,
				),
			),
		)
			.then(() => closeContactSidebar())
			.catch((action: BeneficiaryContactUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'BENEFICIARY_CONTACT_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [currentFacility, beneficiaryId, contactId, formData, dispatch]);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Retiring the designation through the edit form is the prescribed way to transfer it (§2 of the
		// plan): it deserves the same reversible confirmation as any other legal designation revocation.
		if (editedContact?.isPersonOfTrust && !formData.isPersonOfTrust) {
			setIsRevocationConfirmVisible(true);
			return;
		}

		performSubmit();
	}, [formData, editedContact, performSubmit]);

	const handleRevocationDialogHide = React.useCallback(() => {
		setIsRevocationConfirmVisible(false);
	}, []);

	const handleRevocationAccept = React.useCallback(() => {
		setIsRevocationConfirmVisible(false);
		performSubmit();
	}, [performSubmit]);

	const panelHeader = isEditMode ? 'Modifier le contact' : 'Nouveau contact';
	const submitLabel = isEditMode ? 'Enregistrer les modifications' : 'Enregistrer';
	const formAriaLabel = isEditMode ? 'Modifier un contact' : 'Créer un contact';
	const hasEditedContactLoadFailed = isEditMode && editedContact === null;

	const renderFormBody = (): React.ReactNode => {
		if (hasEditedContactLoadFailed) {
			return (
				<Message
					severity="error"
					text="Impossible de charger ce contact. Fermez ce panneau puis réessayez."
				/>
			);
		}

		return (
			<React.Fragment>
				<FormSection title="Identité">
					<InputText
						name="firstName"
						label="Prénom"
						required
						value={formData.firstName}
						onChange={handleFirstNameChange}
						errorMessage={formData.getValidationMessage('firstName')}
						isFormFieldValid={formData.isValid('firstName')}
						autoFocus
					/>
					<InputText
						name="lastName"
						label="Nom"
						required
						value={formData.lastName}
						onChange={handleLastNameChange}
						errorMessage={formData.getValidationMessage('lastName')}
						isFormFieldValid={formData.isValid('lastName')}
					/>
					<Dropdown<string>
						name="relationship"
						label="Lien de parenté"
						required
						options={contactRelationships ?? []}
						optionLabel="label"
						optionValue="value"
						filter
						loading={contactRelationships === null}
						value={formData.relationship}
						onChange={handleRelationshipChange}
						errorMessage={formData.getValidationMessage('relationship')}
						isFormFieldValid={formData.isValid('relationship')}
					/>
				</FormSection>
				<FormSection title="Rôle">
					<Dropdown<string>
						name="role"
						label="Type"
						options={contactRoles ?? []}
						optionLabel="label"
						optionValue="value"
						loading={contactRoles === null}
						showClear
						value={formData.role}
						onChange={handleRoleChange}
						errorMessage={formData.getValidationMessage('role')}
						isFormFieldValid={formData.isValid('role')}
					/>
					{formData.role === OTHER_ROLE_VALUE && (
						<InputText
							name="roleDetails"
							label="Précision du rôle"
							required
							value={formData.roleDetails ?? ''}
							onChange={handleRoleDetailsChange}
							errorMessage={formData.getValidationMessage('roleDetails')}
							isFormFieldValid={formData.isValid('roleDetails')}
						/>
					)}
					<Checkbox
						name="isPersonOfTrust"
						label="Personne de confiance de ce dossier"
						checked={formData.isPersonOfTrust}
						onChange={handleIsPersonOfTrustChange}
						isFormFieldValid={true}
					/>
					{existingTrustHolder !== null && (
						<Message
							severity="info"
							text={`${existingTrustHolder.fullName} est déjà désigné(e) personne de confiance de ce dossier. Une seule personne peut porter cette désignation à la fois.`}
						/>
					)}
				</FormSection>
				<FormSection title="Coordonnées">
					<InputText
						name="phoneNumber"
						label="Téléphone"
						type="tel"
						value={formData.phoneNumber ?? ''}
						onChange={handlePhoneNumberChange}
						errorMessage={formData.getValidationMessage('phoneNumber')}
						isFormFieldValid={formData.isValid('phoneNumber')}
					/>
					<InputText
						name="email"
						label="Courriel"
						type="email"
						value={formData.email ?? ''}
						onChange={handleEmailChange}
						errorMessage={formData.getValidationMessage('email')}
						isFormFieldValid={formData.isValid('email')}
					/>
					<TextArea
						name="notes"
						label="Notes"
						value={formData.notes ?? ''}
						onChange={handleNotesChange}
						errorMessage={formData.getValidationMessage('notes')}
						isFormFieldValid={formData.isValid('notes')}
						rows={4}
					/>
				</FormSection>
			</React.Fragment>
		);
	};

	return (
		<React.Fragment>
			<Sidebar
				visible={context !== null}
				onHide={closeContactSidebar}
				position="right"
				header={panelHeader}
				footer={
					<React.Fragment>
						<Button
							type="button"
							severity="secondary"
							outlined
							label="Annuler"
							onClick={closeContactSidebar}
						/>
						<Button
							type="submit"
							form={formId}
							label={submitLabel}
							loading={isSubmitting}
							disabled={hasEditedContactLoadFailed}
						/>
					</React.Fragment>
				}
				blockScroll
			>
				<Form id={formId} onSubmit={handleSubmit} aria-label={formAriaLabel}>
					{renderFormBody()}
				</Form>
			</Sidebar>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isRevocationConfirmVisible}
				onHide={handleRevocationDialogHide}
				header="Révoquer la désignation"
				message={`${editedContact?.fullName ?? ''} ne sera plus la personne de confiance de ce dossier. Ce dossier n'en aura plus aucune tant que vous n'en désignez pas une autre.`}
				acceptLabel="Révoquer"
				acceptSeverity="danger"
				accept={handleRevocationAccept}
			/>
		</React.Fragment>
	);
};

export default UpsertContactSidebar;
